/**
 * Dinheiro em centavos exatos — o helper único de fronteira (lote D3 · achado FIN-01).
 *
 * `Money` (em `packages/domain`) é o tipo de ARITMÉTICA de dinheiro. Este arquivo é
 * outra coisa: a conversão de um decimal-em-string para um **inteiro exato**, que é o
 * que se compara quando a pergunta é "Σ das partes fecha o total?". Ele mora em
 * `contracts` porque é o único pacote que a API, a SPA interna e a SPA do Portal
 * dividem — e as três precisavam da mesma resposta.
 *
 * O que ele substitui, e por quê:
 *
 *   1. `Math.round(Number(v) * 100)` — seis sítios na API. Reproduzido: o valor
 *      `99999999999999.99` vira `9999999999999998` (perde o centavo) e COLIDE com
 *      `99999999999999.98`. Dois valores distintos e válidos em `numeric(18,2)` ficam
 *      iguais na comparação. Dentro da faixa operacional da agência o double ainda
 *      representa centavos exatos — o dano é de borda —, mas a lei do repositório é
 *      "comparação de dinheiro nunca por float", e uma comparação de igualdade não é
 *      lugar para "provavelmente cabe".
 *   2. `centavos()` em `sicoob/canonico.ts` — correta, e agora é esta.
 *   3. `centavosAssinados()` em `sicoob/arquivo-extrato.ts` — correta, e agora é esta.
 *   4. `centavos()` em `apps/portal/src/main.tsx` — a única **errada** das quatro:
 *      `BigInt('-100') * 100n + BigInt('50')` dava `-9950` em vez de `-10050`, porque
 *      o sinal ficava só na parte inteira. Some junto com as outras.
 *
 * Fração menor que o centavo é RECUSADA, não arredondada: `numeric(18,2)` não a
 * representa, e arredondar em silêncio na fronteira é como o centavo se perde.
 */

/** Erro de fronteira: o texto não é um decimal monetário representável. */
export class ValorMonetarioInvalido extends Error {
  override readonly name = 'ValorMonetarioInvalido'
}

const DECIMAL_MONETARIO = /^(-?)(\d+)(?:\.(\d+))?$/

/**
 * Converte `"1234.56"` em `123456n`. Sem float em nenhum ponto do caminho.
 *
 * @throws {ValorMonetarioInvalido} formato inválido, ou fração abaixo do centavo.
 */
export function centavosDe(valor: string): bigint {
  const partes = DECIMAL_MONETARIO.exec(valor.trim())
  if (partes === null) throw new ValorMonetarioInvalido(`valor monetário inválido: ${JSON.stringify(valor)}`)

  const negativo = partes[1] === '-'
  const inteiros = partes[2] ?? '0'
  const fracao = partes[3] ?? ''

  // Terceira casa em diante só passa se for zero. `1.005` é ambíguo (arredonda para
  // cima? para o par?) e a resposta certa é recusar, não escolher por conta própria.
  if (fracao.length > 2 && fracao.slice(2).replace(/0/g, '') !== '') {
    throw new ValorMonetarioInvalido(`o valor ${valor} contém fração de centavo (menor que 0,01)`)
  }

  const absoluto = BigInt(inteiros) * 100n + BigInt(fracao.padEnd(2, '0').slice(0, 2))
  return negativo ? -absoluto : absoluto
}

/** Caminho de volta: `123456n` → `"1234.56"`. Preserva o sinal na parte inteira. */
export function deCentavos(centavos: bigint): string {
  const negativo = centavos < 0n
  const absoluto = negativo ? -centavos : centavos
  return `${negativo ? '-' : ''}${String(absoluto / 100n)}.${String(absoluto % 100n).padStart(2, '0')}`
}

/** Soma exata de uma lista de decimais-em-string, devolvida como decimal-em-string. */
export function somarDinheiro(valores: readonly string[]): string {
  return deCentavos(valores.reduce((total, valor) => total + centavosDe(valor), 0n))
}

/** `true` quando os dois textos representam exatamente o mesmo dinheiro. */
export function mesmoDinheiro(a: string, b: string): boolean {
  return centavosDe(a) === centavosDe(b)
}

/**
 * Divisão de inteiros com arredondamento **meio para cima** (empate vai para +∞) —
 * exatamente a regra do `Math.round`, mas sem passar por float.
 *
 * Existe porque as divisões de dinheiro que sobraram no código (ritmo diário,
 * projeção, fração de atingimento) eram `Math.round(a / b)` com `a` e `b` já em
 * centavos: a conta é exata em inteiros, e só o `/` a jogava para `number`.
 *
 * O `/` do BigInt trunca em direção a ZERO, então o ramo negativo não pode usar a
 * mesma expressão do positivo — daí a assimetria abaixo, que é deliberada.
 */
export function dividirCentavos(numerador: bigint, denominador: bigint): bigint {
  if (denominador === 0n) throw new ValorMonetarioInvalido('divisão por zero')
  const n = denominador < 0n ? -numerador : numerador
  const d = denominador < 0n ? -denominador : denominador
  const dobro = 2n * n + d
  const divisor = 2n * d
  return dobro >= 0n ? dobro / divisor : -((-dobro + divisor - 1n) / divisor)
}

const FRACAO_DE_PERCENTUAL = /^(-?)(\d+)(?:\.(\d+))?$/

/**
 * A fração do banco vira o percentual que a pessoa lê — a BORDA da convenção (D-E).
 *
 * `dv_pct` é `numeric(9,6)` e guarda fração (`0.13` = 13%, dito com todas as letras em
 * `0001_fundacao.sql:44`). A decisão de 05/09/2026 é que o fio carrega o que o banco
 * guarda e a multiplicação por cem acontece **uma vez, na apresentação** — aqui.
 *
 * Ela mora em `contracts` pela mesma razão que `centavosDe`: é o único pacote que a API,
 * o worker, a SPA interna e a SPA do Portal dividem, e as quatro precisavam da mesma
 * resposta. Cada cópia que existiu errou de um jeito diferente — o Portal não escalava
 * nada (13% saía "0,13%"), a DRE também não ("ISS 0,025000%" onde a alíquota é 2,5%), e
 * um formulário usava `Number(f) * 100`, que devolve `7.000000000000001` para sete por
 * cento. Nenhuma delas passa por float: a conta é o ponto decimal andando duas casas.
 *
 * **O sinal é parte do valor.** O guarda anterior era `/^\d+(\.\d+)?$/`, e uma margem
 * negativa devolvia `''` — que `Number('')` transforma em ZERO. Uma campanha a −30%
 * aparecia como 0% na tela que existe para avisar do prejuízo.
 *
 * Texto que não é decimal devolve `''`: a borda não inventa número. E `-0` não existe —
 * zero negativo é zero.
 */
export function fracaoParaPercentual(fracao: string): string {
  const partes = FRACAO_DE_PERCENTUAL.exec(fracao)
  if (partes === null) return ''
  const sinal = partes[1] ?? ''
  const inteiro = partes[2] ?? '0'
  const decimais = partes[3] ?? ''
  const digitos = `${inteiro}${decimais}`.replace(/^0+(?=\d)/, '')
  const casas = decimais.length - 2
  const bruto = casas <= 0
    ? `${digitos}${'0'.repeat(-casas)}`
    : `${digitos.padStart(casas + 1, '0').slice(0, -casas)}.${digitos.padStart(casas + 1, '0').slice(-casas)}`
  const semZerosAtras = bruto.includes('.')
    ? bruto.replace(/0+$/, '').replace(/\.$/, '')
    : bruto
  const absoluto = semZerosAtras.replace(/^0+(?=\d)/, '') || '0'
  return absoluto === '0' ? '0' : `${sinal}${absoluto}`
}
